import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { EditOutlined, DeleteOutline } from "@mui/icons-material";
import { collection, getDocs, doc, setDoc, deleteDoc, Timestamp } from "firebase/firestore";
import Swal from "sweetalert2";
import { db } from "../../firebase";

const PAGE_SIZE = 10;

function Spliexpense() {
    const [expenses, setExpenses] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");

    const [form, setForm] = useState({
        title: "",
        monthlyAmount: "",
        dailyAmount: "",
    });

    const [openDialog, setOpenDialog] = useState(false);

    // ================= Load Expenses =================
    useEffect(() => {
        loadExpenses();
    }, []);

    const loadExpenses = async () => {
        try {
            const snap = await getDocs(collection(db, "splitExpenses"));
            const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setExpenses(arr);
        } catch (error) {
            Swal.fire("Error", "Failed to fetch data: " + error.message, "error");
        }
    };

    // ================= Form change handler =================
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleOpenAdd = () => {
        setForm({ title: "", monthlyAmount: "", dailyAmount: "" });
        setEditingId(null);
        setOpenDialog(true);
    };

    // ================= Save / Update =================
    const saveExpense = async () => {
        if (!form.title || !form.monthlyAmount || !form.dailyAmount) {
            Swal.fire("Error", "Please fill all required fields", "error");
            return;
        }

        try {
            const id = editingId || doc(collection(db, "splitExpenses")).id;
            await setDoc(doc(db, "splitExpenses", id), {
                ...form,
                monthlyAmount: Number(form.monthlyAmount),
                dailyAmount: Number(form.dailyAmount),
                createdTime: editingId ? form.createdTime : Timestamp.now(),
            });

            Swal.fire("Success", editingId ? "Expense updated" : "Expense added", "success");

            setForm({ title: "", monthlyAmount: "", dailyAmount: "" });
            setEditingId(null);
            setOpenDialog(false);
            loadExpenses();
        } catch (error) {
            Swal.fire("Error", error.message, "error");
        }
    };

    // ================= Edit =================
    const editExpense = (expense) => {
        setForm(expense);
        setEditingId(expense.id);
        setOpenDialog(true);
    };

    // ================= Delete =================
    const deleteExpense = async (id) => {
        const res = await Swal.fire({
            title: "Delete this expense?",
            icon: "warning",
            showCancelButton: true,
        });

        if (!res.isConfirmed) return;

        try {
            await deleteDoc(doc(db, "splitExpenses", id));
            Swal.fire("Deleted", "Expense removed", "success");
            loadExpenses();
        } catch (error) {
            Swal.fire("Error", error.message, "error");
        }
    };

    // ================= Filter & Pagination =================
    const filtered = expenses.filter((exp) =>
        exp.title?.toLowerCase().includes(search.toLowerCase())
    );
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <Box minHeight="100vh" bgcolor="#f4f6f8">
            <Box flex={1} p={3}>

                {/* Title Block */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h5" fontWeight={800}>
                        Split Expenses Management
                    </Typography>
                    <Button variant="contained" onClick={handleOpenAdd}>
                        Add New Expense
                    </Button>
                </Box>

                {/* Searchbox */}
                <TextField
                    label="Search by title"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                    }}
                    sx={{ mb: 3, width: "100%" }}
                    size="small"
                />

                {/* Table */}
                <Card variant="outlined">
                    <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                        <Table size="medium">
                            <TableHead sx={{ bgcolor: "#fafafa" }}>
                                <TableRow>
                                    <TableCell style={{ fontWeight: 600 }}>Sr No</TableCell>
                                    <TableCell style={{ fontWeight: 600 }}>Title</TableCell>
                                    <TableCell style={{ fontWeight: 600 }} align="right">Monthly Amount</TableCell>
                                    <TableCell style={{ fontWeight: 600 }} align="right">Daily Amount</TableCell>
                                    <TableCell style={{ fontWeight: 600 }} align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {paginated.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                                            No data available
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginated.map((exp, index) => (
                                        <TableRow key={exp.id} hover>
                                            {/* Auto-Calculated Serial Number based on page */}
                                            <TableCell>{(page - 1) * PAGE_SIZE + index + 1}</TableCell>
                                            <TableCell>{exp.title}</TableCell>
                                            <TableCell align="right">{Number(exp.monthlyAmount || 0).toFixed(2)}</TableCell>
                                            <TableCell align="right">{Number(exp.dailyAmount || 0).toFixed(2)}</TableCell>
                                            <TableCell align="center">
                                                <IconButton color="primary" onClick={() => editExpense(exp)}>
                                                    <EditOutlined />
                                                </IconButton>
                                                <IconButton color="error" onClick={() => deleteExpense(exp.id)}>
                                                    <DeleteOutline />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>

                        {/* Pagination */}
                        {filtered.length > PAGE_SIZE && (
                            <Box display="flex" justifyContent="center" my={3}>
                                <Pagination
                                    count={Math.ceil(filtered.length / PAGE_SIZE)}
                                    page={page}
                                    onChange={(e, v) => setPage(v)}
                                    color="primary"
                                />
                            </Box>
                        )}
                    </CardContent>
                </Card>

                {/* Dialog Form Fields */}
                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="xs">
                    <DialogTitle fontWeight={700}>
                        {editingId ? "Edit Split Expense" : "Add Split Expense"}
                    </DialogTitle>
                    <DialogContent dividers>
                        <Box display="flex" flexDirection="column" gap={2.5} mt={1}>
                            <TextField
                                label="Title"
                                name="title"
                                value={form.title}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                            />
                            <TextField
                                label="Monthly Amount"
                                name="monthlyAmount"
                                type="number"
                                value={form.monthlyAmount}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                            />
                            <TextField
                                label="Daily Amount"
                                name="dailyAmount"
                                type="number"
                                value={form.dailyAmount}
                                onChange={handleChange}
                                fullWidth
                                required
                                size="small"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, py: 2 }}>
                        <Button onClick={() => setOpenDialog(false)} color="inherit">
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={saveExpense}>
                            {editingId ? "Update" : "Add"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
}

export default Spliexpense;